import { AnyQueueData, QueueStatus, QueueStatusData, truncateRequestId } from '../hooks/queue/queueTypes'

export type QueueListener = () => void

/**
 * The queue store keeps track of long-running jobs (document generation, email filing, time suggestions) that are
 * dispatched to the server and reported back on asynchronously over the websocket.
 *
 * It is deliberately a plain object rather than React state: consumers subscribe per job id through
 * useSyncExternalStore, so a mutation on one job only re-renders the components that care about that job. The store
 * object itself is created once and never replaced, which means the context value never changes identity and the
 * provider never re-renders.
 */
export type QueueStore = {
    // Reads. These are synchronous and safe to call outside of render.
    get<DataT>(id: string | undefined): DataT | undefined
    has(id: string | undefined): boolean
    // The returned array is cached and only replaced when the set of ids actually changes, because
    // useSyncExternalStore requires getSnapshot() to be referentially stable between notifications.
    ids(): readonly string[]

    // Subscriptions. Both return an unsubscribe function.
    subscribe(id: string | undefined, listener: QueueListener): () => void
    subscribeToIds(listener: QueueListener): () => void

    // Writes.
    push<DataT>(id: string, data?: DataT): void
    // Merges into the existing entry. A no-op when the entry does not exist.
    update<DataT>(id: string, data?: Partial<DataT>): void
    remove(id: string): void

    /**
     * Moves a job to its next status. This is the single place where a status transition happens: the status builds
     * on top of the previous one, and a transition for a job that is no longer queued is silently ignored, because
     * notifications can arrive after the job has been cancelled or completed.
     */
    transition<StatusT>(requestId: string, next: QueueStatus, status?: Partial<QueueStatusData<StatusT>> | Pick<QueueStatusData, 'error'>): void
}

export function createQueueStore(): QueueStore {
    const items = new Map<string, unknown>()
    const listeners = new Map<string, Set<QueueListener>>()
    const idListeners = new Set<QueueListener>()

    // Cached snapshot of the key set, see ids().
    let idsSnapshot: readonly string[] = []

    const notify = (id: string) => listeners.get(id)?.forEach(listener => listener())

    const set = (id: string, data: unknown) => {
        items.set(id, data)
        notify(id)
    }

    const notifyIds = () => {
        idsSnapshot = Array.from(items.keys())
        idListeners.forEach(listener => listener())
    }

    return {
        get<DataT>(id: string | undefined): DataT | undefined {
            return id === undefined ? undefined : (items.get(id) as DataT | undefined)
        },

        has(id: string | undefined): boolean {
            return id !== undefined && items.has(id)
        },

        ids(): readonly string[] {
            return idsSnapshot
        },

        subscribe(id: string | undefined, listener: QueueListener): () => void {
            if (id === undefined) {
                return () => undefined
            }

            const set = listeners.get(id) || new Set<QueueListener>()
            listeners.set(id, set)
            set.add(listener)

            return () => {
                set.delete(listener)
                if (set.size === 0) {
                    listeners.delete(id)
                }
            }
        },

        subscribeToIds(listener: QueueListener): () => void {
            idListeners.add(listener)
            return () => idListeners.delete(listener)
        },

        push<DataT>(id: string, data?: DataT) {
            const isNew = !items.has(id)
            set(id, data)
            // Only the set of ids changed when this is a new entry; re-pushing an existing id is an update.
            if (isNew) {
                notifyIds()
            }
        },

        update<DataT>(id: string, data?: Partial<DataT>) {
            if (!items.has(id)) {
                return
            }
            // Merge, so that fields the caller does not know about survive the update.
            set(id, { ...(items.get(id) as object), ...(data as object) })
        },

        remove(id: string) {
            if (!items.delete(id)) {
                return
            }
            notify(id)
            notifyIds()
        },

        transition<StatusT>(requestId: string, next: QueueStatus, status?: Partial<QueueStatusData<StatusT>> | Pick<QueueStatusData, 'error'>) {
            const id = truncateRequestId(requestId)
            const previous = items.get(id) as AnyQueueData | undefined

            if (previous === undefined) {
                return
            }

            set(id, {
                ...previous,
                // The status expands and builds on top of the previous status; the context never changes after
                // initialisation.
                status: { ...previous.status, current: next, ...(status || {}) },
            })
        },
    }
}
